import React, { useRef } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import { findIcon } from "../../views/dashboard/icons";
import Skeleton from "../skeleton";
import PageNotFound from "../../views/PageNotFound";
import { GraphCardsHeader } from "@ui";

const specialtyColorMap = {
  "covid-19": "#dc5353",
  "allergy & immunology": "#6a871f",
  endocrinology: "#574f8c",
  hematology: "#890a07",
  nephrology: "#6285b9",
  "general surgery": "#a71351",
  general: "#355fd3",
  hepatology: "#964b00",
  "pain management": "#8d4884",
  "obstetrics & gynaecology": "#ba1e5f",
  dermatology: "#bc6c1b",
  pathology: "#a932c7",
  orthopaedics: "#b36512",
  pediatrics: "#1278b7",
  gastroenterology: "#b33d00",
  urology: "#4545e2",
  "pathology and radiology": "#6f52ce",
  pulmonology: "#37b989",
  "critical care": "#f44141",
  rheumatology: "#af3252",
  ophthalmology: "#209c51",
  anesthesiology: "#191970",
  oncology: "#d33f8f",
  psychiatry: "#5d4e8b",
  cardiology: "#700505",
  neurology: "#9370db",
  microbiology: "#53975a",
  "internal medicine": "#4b24cd",
  "general medicine": "#a71351",
  diabetology: "#1b9381",
  ent: "#1bb3b2",
  "onco surgery": "#aa3b72",
};

function MixedLineChart({
  series,
  labels,
  exportCSVHeading = "Date,",
  height = 300,
  markerSize,
  chartTitle,
  loading = false,
  hasData = true,
}) {
  const chartRef = useRef(null);

  const chartOptions = {
    series: series,
    colors: series.map(
      (s) => specialtyColorMap[s.name.toLowerCase()] || "#3256C1"
    ),
    chart: {
      type: "line",
      height: 350,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
    },
    markers: {
      size: "11px" || 0,
    },

    labels: labels || [],
    xaxis: {
      categories: labels || [],
      tickAmount: 5,
      labels: {
        show: true,
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        showDuplicates: false,
        trim: false,
      },
    },
    yaxis: {
      title: {
        text: "Percentage",
      },
    },
    legend: {
      show: true,
      horizontalAlign: "left",
      fontSize: "11px",
    },
    tooltip: {
      shared: true,
      custom: function ({ series, dataPointIndex, w }) {
        let xLabel = w.globals.categoryLabels[dataPointIndex];

        let tooltipHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      gap: 5px;
      padding: 8px;
      max-width: 100%;
      font-family: sans-serif;
    ">
      <div style="
        font-size: 11px;
        font-weight: bold;
        border-bottom: 1px solid #ccc;
        padding-bottom: 5px;
      ">${xLabel}</div>
      <div style="
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
      ">`;

        series.forEach((s, i) => {
          tooltipHTML += `
        <div style="
          display: flex;
          align-items: flex-start;
          gap: 5px;
          font-size: 11px;
          word-break: break-word;
          flex: 1 1 auto;
        ">
          <span style="
            width: 8px;
            height: 8px;
            margin-top: 3px;
            border-radius: 50%;
            background-color: ${w.globals.colors[i]};
            flex-shrink: 0;
          "></span>
         
          <span style="font-weight: 500;">${w.globals.seriesNames[i]}: </span>
          <span>${s[dataPointIndex]}%</span>
        </div>`;
        });

        tooltipHTML += `</div></div>`;
        return tooltipHTML;
      },
    },
  };

  function exportCSV() {
    let csvContent =
      exportCSVHeading + series.map((s) => s.name).join(",") + "\n";

    labels.forEach((category, index) => {
      let row = [category];
      series.forEach((series) => {
        row.push(series.data[index]);
      });
      csvContent += row.join(",") + "\n";
    });
    var blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    var link = document.createElement("a");
    if (link.download !== undefined) {
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "chart_data.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  return (
    <Card className="h-100 m-0">
      <GraphCardsHeader
        title={chartTitle}
        showDownloadButtons={true}
        needCSVDownload={true}
        downloadCSV={exportCSV}
      />
      <div className="d-flex flex-column">
        <Card.Body className="p-0">
          {!loading ? (
            hasData ? (
              <div>
                <div id="chart-basic-column-bar">
                  <Chart
                    ref={chartRef}
                    options={chartOptions}
                    series={chartOptions.series}
                    height={height}
                  />
                </div>
              </div>
            ) : (
              <PageNotFound
                title="No data found"
                backgroundImage={findIcon("Empty-folder", "dual-tone", 100)}
              />
            )
          ) : (
            <div className="p-3">
              <Skeleton
                variant="square"
                width={"100%"}
                height={300}
                className="rounded-3"
              />
            </div>
          )}
        </Card.Body>
      </div>
    </Card>
  );
}

export default MixedLineChart;
